import { FC, useState } from "react";
import { Table, Button, Tag, Input, Select, Card } from "antd";
import { useNavigate } from "react-router-dom";
import { FileText, Search, Zap, Calendar, User, ArrowRight } from "lucide-react";
import dayjs from "dayjs";
import { useGetEstimates } from "~/services/estimate";
import { EstimateStatus } from "~/types/estimate";
import { useBooking } from "~/store";

const { Search: SearchInput } = Input;

export const EstimatesList: FC = () => {
  const navigate = useNavigate();
  const { openBookingModal } = useBooking();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<EstimateStatus | undefined>();

  const { data: estimatesData, isLoading } = useGetEstimates({
    page,
    pageSize,
    search,
    status: statusFilter,
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  const getStatusColor = (status: EstimateStatus) => {
    const colors = {
      [EstimateStatus.DRAFT]: "default",
      [EstimateStatus.SENT]: "blue",
      [EstimateStatus.ACCEPTED]: "green",
      [EstimateStatus.REJECTED]: "red",
      [EstimateStatus.CONVERTED]: "purple",
    };
    return colors[status];
  };

  const getStatusText = (status: EstimateStatus) => {
    return status.charAt(0) + status.slice(1).toLowerCase();
  };

  const columns = [
    {
      title: "Reference #",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      render: (text: string, record: any) => (
        <div className="flex items-center space-x-2">
          <FileText size={16} className="text-gray-400" />
          <span className="font-medium">{text}</span>
          {record.tempClientData && (
            <Tag color="orange" icon={<Zap size={10} />} className="ml-2">
              Quick
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Client",
      key: "client",
      render: (_: any, record: any) => {
        if (record.tempClientData) {
          return (
            <div>
              <div className="font-medium">{record.tempClientData.name}</div>
              <div className="text-xs text-gray-500">Temporary Contact</div>
            </div>
          );
        }
        if (record.client) {
          return (
            <div>
              <div className="font-medium">
                {record.client.companyName ||
                  `${record.client.firstName} ${record.client.lastName}`.trim() ||
                  "Unnamed Client"}
              </div>
              <div className="text-xs text-gray-500">{record.client.email}</div>
            </div>
          );
        }
        return <span className="text-gray-400">No Client</span>;
      },
    },
    {
      title: "Services",
      dataIndex: "services",
      key: "services",
      render: (services: any[]) => (
        <span className="text-gray-600">{services?.length || 0} services</span>
      ),
    },
    {
      title: "Date",
      dataIndex: "estimateDate",
      key: "estimateDate",
      render: (date: string) => (
        <div className="flex items-center space-x-2 text-gray-600">
          <Calendar size={14} />
          <span>{dayjs(date).format("MMM DD, YYYY")}</span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: EstimateStatus) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => (
        <Button
          type="link"
          onClick={() => navigate(`/estimates/${record.id}`)}
          icon={<ArrowRight size={16} />}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold">Estimates</h1>
            <p className="text-gray-500">Manage and track all estimates</p>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<Zap size={16} />}
            onClick={() => openBookingModal({ isEstimateMode: true })}
            className="bg-green-600 hover:bg-green-700"
          >
            Create Quick Estimate
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <div className="flex gap-4">
            <SearchInput
              placeholder="Search by reference number or client..."
              allowClear
              size="large"
              prefix={<Search size={16} />}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 400 }}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              size="large"
              style={{ width: 200 }}
              onChange={setStatusFilter}
              options={[
                { value: EstimateStatus.DRAFT, label: "Draft" },
                { value: EstimateStatus.SENT, label: "Sent" },
                { value: EstimateStatus.ACCEPTED, label: "Accepted" },
                { value: EstimateStatus.REJECTED, label: "Rejected" },
                { value: EstimateStatus.CONVERTED, label: "Converted" },
              ]}
            />
          </div>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={estimatesData?.items || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: estimatesData?.meta?.totalItems || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} estimates`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            },
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/estimates/${record.id}`),
            className: "cursor-pointer hover:bg-gray-50",
          })}
        />
      </Card>
    </div>
  );
};
